import { useEffect, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useUserProgram } from '@/hooks/useUserProgram';
import { ProgramCard } from './ProgramCard';
import { Loader2 } from 'lucide-react';
import { getProgramActivityCounts } from '@/lib/api';
import { useProduct, getProductNamespace } from '@/hooks/useProduct';

interface ProgramWithCount {
  id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  is_active?: boolean | null;
  activityCount: number;
}

interface ProgramCarouselProps {
  onProgramChange?: () => void;
}

export function ProgramCarousel({ onProgramChange }: ProgramCarouselProps) {
  const { t } = useTranslation();
  const { program: activeProgram, allPrograms, switchProgram, loading: programLoading } = useUserProgram();
  const { productId } = useProduct();
  const productNs = getProductNamespace(productId);
  const [programsWithCounts, setProgramsWithCounts] = useState<ProgramWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [switchingToId, setSwitchingToId] = useState<string | null>(null);

  useEffect(() => {
    fetchActivityCounts();
  }, [allPrograms]);

  const fetchActivityCounts = async () => {
    if (allPrograms.length === 0) {
      setProgramsWithCounts([]); // Clear old programs when no programs available
      setLoading(false);
      return;
    }

    try {
      const programIds = allPrograms.map(p => p.id);
      const result = await getProgramActivityCounts(programIds);

      if (result.success) {
        const enriched = allPrograms.map(p => ({
          ...p,
          activityCount: result.data[p.id] || 0,
        }));
        setProgramsWithCounts(enriched);
      } else {
        // Fallback without counts
        setProgramsWithCounts(allPrograms.map(p => ({ ...p, activityCount: 0 })));
      }
    } catch (error) {
      console.error('Error fetching activity counts:', error);
      // Fallback without counts
      setProgramsWithCounts(allPrograms.map(p => ({ ...p, activityCount: 0 })));
    } finally {
      setLoading(false);
    }
  };

  const handleProgramClick = async (programId: string) => {
    if (activeProgram?.id === programId || switching) return;
    
    setSwitching(true);
    setSwitchingToId(programId);
    
    try {
      await switchProgram(programId);
      onProgramChange?.();
    } finally {
      // Small delay to show the transition
      setTimeout(() => {
        setSwitching(false);
        setSwitchingToId(null);
      }, 300);
    }
  };

  if (loading || programLoading) {
    return (
      <div className="mt-8">
        <h3 className="font-semibold text-lg mb-4">{t(`${productNs}:program.availablePrograms`, 'Available Programs')}</h3>
        <div className="flex gap-4 overflow-hidden">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="w-[200px] h-[160px] rounded-lg flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  // Hide carousel if no programs available in the current language
  if (programsWithCounts.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-semibold text-lg">
          {t(`${productNs}:program.availablePrograms`, 'Available Programs')}
        </h3>
        {switching && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground animate-fade-in">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t(`${productNs}:program.switching`, 'Switching program...')}</span>
          </div>
        )}
      </div>
      
      <div className="relative px-2 sm:px-10">
        <Carousel
          opts={{
            align: 'start',
            loop: programsWithCounts.length > 2,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 sm:-ml-4">
            {programsWithCounts.map((program) => (
              <CarouselItem key={program.id} className="pl-2 sm:pl-4 basis-[45%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                <div className={`transition-all duration-300 ${
                  switching && switchingToId === program.id 
                    ? 'scale-[0.98] opacity-80' 
                    : switching 
                      ? 'opacity-50' 
                      : ''
                }`}>
                  <ProgramCard
                    program={program}
                    isActive={activeProgram?.id === program.id}
                    activityCount={program.activityCount}
                    onClick={() => handleProgramClick(program.id)}
                    isLoading={switching && switchingToId === program.id}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -left-2 sm:-left-4" />
          <CarouselNext className="hidden sm:flex -right-2 sm:-right-4" />
        </Carousel>
      </div>
    </div>
  );
}
